import { defineComponent } from 'vue'
import type { Account } from '@/store'
import type { User } from '@/store/users'
import type { Crisp } from '@/index'
import type { Integration } from '@/store/integrations'

export const LoadCrispMixin = defineComponent({
  methods: {
    loadCrisp() {
      if (process.env.VUE_APP_CRISP_WEBSITE_ID) {
        window.$crisp = [] as unknown as Crisp
        window.CRISP_WEBSITE_ID = process.env.VUE_APP_CRISP_WEBSITE_ID
        const d = document
        const s = d.createElement('script')
        s.src = 'https://client.crisp.chat/l.js'
        s.async = false
        d.getElementsByTagName('head')[0].appendChild(s)

        // Add a ready event handler to properly initialize after Crisp is loaded
        window.$crisp.push([
          'on',
          'session:loaded',
          () => {
            window.$crisp.push(['do', 'chat:close'])
            // Get the message shown status properly after Crisp is ready
            const value = window.$crisp.get(
              'session:data',
              'show_script_tag_support_message',
            ) as boolean
            this.$store.dispatch('setScriptTagSupportMessageShown', value)
          },
        ])
      }
    },
    openChat(defaultMessage = '') {
      window.$crisp.push(['do', 'chat:open'])
      // Add users email
      if (this.currentUser.email) {
        window.$crisp.push(['set', 'user:email', [this.currentUser.email]])
      }
      // add users name and company
      if (this.currentUser.firstName && this.currentUser.lastName) {
        window.$crisp.push([
          'set',
          'user:nickname',
          [
            `${this.currentUser.firstName} ${this.currentUser.lastName} ${
              this.account?.companyName ? `(${this.account?.companyName})` : ''
            }`,
          ],
        ])
      }

      if (defaultMessage) window.$crisp.push(['set', 'message:text', [defaultMessage]])
    },
    showScriptTagSupportMessage(isAutomationNeeded = true) {
      if (this.hasScriptTagSupportMessageShown || !this.getShowScriptTagSupportMessageFeatureFlag) {
        return
      }

      const isIntegrationPreconditionMet =
        this.shopifyIntegrations?.length === 1 &&
        (isAutomationNeeded ? this.shopifyIntegrations[0]?.automationList?.length === 1 : true)
      if (!isIntegrationPreconditionMet) {
        return
      }
      window.$crisp.push(['set', 'session:data', [[['show_script_tag_support_message', 'true']]]])
      this.$store.dispatch('setScriptTagSupportMessageShown', true)

      window.$crisp.push([
        'do',
        'message:show',
        [
          'text',
          `👋 Hey${this.currentUser.firstName ? ` ${this.currentUser.firstName}` : ''}!

              Great to see you've connected your Shopify store! 🎉

              To make things even easier, we'd love to offer you a personalised onboarding and set up your Greenspark widgets exactly how you'd like - completely for free. ✨

              All we need is a few simple details and we can get you set-up:

              1. 🖥️ Widget Details
              * Widget types (Full-width banner, Product, Cart, etc)
              * Preferred colours for each one (or background image for the banner) 🎨
              * Where you'd like them placed (homepage, product pages, checkout, etc.)
              * Screenshots are always helpful too 📸
              * Any special requests? If we can make it happen, we will!

              2. 🔑 Store Access
              * Your collaborator code (if enabled)
              * Keep an eye out for our access request in your Shopify dashboard and simply click accept ✅

              Of course we will create a duplicate of your theme so that you can check it out before publishing.

              Any questions, just let us know, we're here to help! 🙌

              Cheers,
              Lenny & the Greenspark Team 🌱`,
        ],
      ])
    },
  },
  computed: {
    account(): Account {
      return this.$store.getters['getAccount']
    },
    currentUser(): User {
      return this.$store.getters['getCurrentUser']
    },
    hasScriptTagSupportMessageShown(): boolean {
      return this.$store.getters['getScriptTagSupportMessageStatus']
    },
    getShowScriptTagSupportMessageFeatureFlag(): boolean {
      return this.$store.getters['getShowScriptTagSupportMessageFeatureFlag']
    },
    shopifyIntegrations(): Integration[] {
      return this.$store.getters['getIntegrationsByPlatform']('shopify')
    },
  },
})
